'use client';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import MainCarousel from './MainCarousel';
import Footer from './Footer';
import Header from './Header';
import Link from 'next/link';
import ScrollTopButton from './ScrollTopButton';

export default function ClientHome({ news, gallery }) {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [currentImage, setCurrentImage] = useState(0);
  const sectionRef = useRef(null);

  const droneImages = [
    '/images/drone/mainHeader1.jpg',
    '/images/drone/mainHeader2.jpg',
    '/images/drone/mainHeader3.jpg',
    '/images/drone/seeker1.png',
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // 드론 이미지 자동 캐러셀
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % droneImages.length);
    }, 2000); // 2초마다 변경

    return () => clearInterval(interval);
  }, [droneImages.length]);

  // Intersection Observer for the main images section (About 섹션 안의 아이콘들)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            MAINIMAGE.forEach((image, index) => {
              setTimeout(() => {
                setVisibleItems((prev) => new Set([...prev, image.id]));
              }, index * 200);
            });
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '-50px 0px -50px 0px',
      },
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  // ✅ Hero 버튼: "The innovative leader in / KD darts" 타이틀 위치로 이동
  const scrollToAbout = () => {
    const el = document.getElementById('innovative-anchor');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="font-sans min-h-screen">
      <ScrollTopButton />
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden min-h-screen bg-black">
        {/* 배경 이미지 캐러셀 */}
        <div className="absolute inset-0 z-0">
          {droneImages.map((src, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                currentImage === index ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={src}
                alt={`Background Drone ${index + 1}`}
                className="w-full h-full object-cover object-center"
              />
              {/* 모바일용 어두운 오버레이 */}
              <div className="absolute inset-0 bg-black/40 md:bg-black/20" />
            </div>
          ))}
        </div>

        {/* Hero Text */}
        <div className="relative z-20 text-center md:text-left text-white px-6 md:px-16 max-w-4xl mx-auto md:mx-0 flex flex-col justify-end h-full min-h-screen pb-24 md:pb-20">
          <h1
            className={`text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black mb-4 md:mb-6 tracking-tight transform transition-all duration-1000 ${
              isVisible
                ? 'translate-y-0 opacity-100'
                : 'translate-y-8 opacity-0'
            }`}
          >
            <span className="text-gray-200 inline-block">KD darts</span>
          </h1>

          <p
            className={`text-sm sm:text-base md:text-lg mb-6 md:mb-8 opacity-90 leading-relaxed transform transition-all duration-1000 delay-300 ${
              isVisible ? 'translate-y-0 opacity-90' : 'translate-y-8 opacity-0'
            }`}
          >
            <span className="block mb-2">
              AI 기반 자율 비행과 실시간 환경 인식 기술로
            </span>
            <span className="block mb-2">
              국방·산업·재난 현장의 한계를 넘어섭니다.
            </span>
            <span className="block mt-4 mb-1">
              ㈜케이디다츠는 검증된 핵심 기술력과
            </span>
            <span className="block">
              끊임없는 혁신으로 대한민국 무인 항공 시스템의
            </span>
            <span className="block">새로운 기준을 제시합니다.</span>
          </p>

          {/* 인디케이터 */}
          <div className="flex justify-center md:justify-start gap-2 mt-4 md:mt-8">
            {droneImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-2 h-2 rounded-full transition-all duration-900 ${
                  currentImage === index
                    ? 'bg-white w-8'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`드론 이미지 ${index + 1}로 이동`}
              />
            ))}
          </div>
        </div>

        {/* 아래로 이동 버튼 */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <button
            onClick={scrollToAbout}
            className="animate-bounce hover:scale-110 transition-transform duration-300 cursor-pointer p-2 rounded-full hover:bg-white/10"
            aria-label="스크롤하여 더 보기"
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </button>
        </div>
      </section>

      {/* ===================== "The innovative leader in KD darts" 섹션 ===================== */}
      <section className="px-4 mx-auto pt-52 sm:px-6 lg:px-44 bg-zinc-950 min-h-screen relative overflow-hidden">
        <style jsx>{`
          @keyframes float {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }
        `}</style>

        <div className="mx-auto relative">
          {/* ✅ Hero 버튼이 정확히 이 타이틀 위치로 오게 하는 앵커 */}
          {/* -top 값을 조절하면 더 위/아래로 미세 조정 가능 */}
          <div id="innovative-anchor" className="relative -top-6" />

          {/* 배경 워터마크 텍스트 */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
            <h1 className="text-[8rem] md:text-[12rem] lg:text-[16rem] xl:text-[18rem] font-black text-gray-800 opacity-50 whitespace-nowrap">
              KD darts
            </h1>
          </div>

          {/* 메인 타이틀 */}
          <div className="mb-8 lg:mb-24 relative z-10">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-gray-200 leading-tight">
              The innovative leader in
              <br />
              <span className="text-blue-600 font-black">KD darts</span>
            </h2>
          </div>

          {/* 모바일/태블릿: 카드 그리드 레이아웃 */}
          <div className="lg:hidden relative z-10 space-y-6 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg font-bold text-gray-100 mb-2">
                    무인기 플랫폼 설계 및 제작기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    UAV Platform Design
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    무인기 플랫폼 설계 및 제작 기술로 다양한 임무 수행이 가능한
                    드론 시스템을 개발합니다.
                  </p>
                </div>
              </div>

              <div>
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg font-bold text-gray-100 mb-2">
                    무인기 항법 시스템 설계 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Navigation System
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    정밀한 항법 시스템으로 목표 지점까지 정확하게 비행할 수 있는
                    기술을 제공합니다.
                  </p>
                </div>
              </div>

              <div>
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg font-bold text-gray-100 mb-2">
                    김벌 설계 및 제작 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Gimbal Technology
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    안정적인 촬영과 정밀한 목표 추적을 위한 고성능 김벌 시스템을
                    개발합니다.
                  </p>
                </div>
              </div>

              <div>
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg font-bold text-gray-100 mb-2">
                    자율 유도비행 제어 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Autonomous Flight Control
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    AI 기반 자율 비행 제어로 복잡한 임무를 자동으로 수행할 수
                    있습니다.
                  </p>
                </div>
              </div>

              <div>
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg font-bold text-gray-100 mb-2">
                    표적 자동인식 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Target Recognition
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    딥러닝 기반 표적 자동 인식으로 빠르고 정확한 식별이
                    가능합니다.
                  </p>
                </div>
              </div>

              <div>
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg font-bold text-gray-100 mb-2">
                    군집비행 제어 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Swarm Control
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    다수의 드론을 동시에 제어하여 협력 임무를 수행하는 군집 제어
                    기술입니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 모바일 이미지 섹션 */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-900/30 backdrop-blur-sm rounded-lg p-4 flex items-center justify-center">
                <img
                  src="/images/drone/OBJ2.png"
                  alt="Main Drone"
                  className="w-full max-w-[500px] h-auto object-contain"
                  style={{ animation: 'float 3s ease-in-out infinite' }}
                />
              </div>
            </div>
          </div>

          {/* 데스크톱 레이아웃 */}
          <div className="hidden lg:block relative min-h-[400px] xl:min-h-[400px]">
            <div className="absolute left-0 top-0 w-[280px] xl:w-[320px] z-20">
              <div className="p-5">
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-xl font-bold text-gray-100 mb-2">
                    무인기 플랫폼 설계 및 제작기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    UAV Platform Design
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    무인기 플랫폼 설계 및 제작 기술로 다양한 임무 수행이 가능한
                    드론 시스템을 개발합니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute left-[310px] xl:left-[350px] top-0 w-[280px] xl:w-[320px] z-20">
              <div className="p-5">
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-xl font-bold text-gray-100 mb-2">
                    무인기 항법 시스템 설계 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Navigation System
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    정밀한 항법 시스템으로 목표 지점까지 정확하게 비행할 수 있는
                    기술을 제공합니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute left-0 top-[220px] xl:top-[240px] w-[220px] xl:w-[250px] z-20">
              <div className="p-5">
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg xl:text-xl font-bold text-gray-100 mb-2">
                    김벌 설계 및 제작 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Gimbal Technology
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    안정적인 촬영과 정밀한 목표 추적을 위한 고성능 김벌 시스템을
                    개발합니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute left-[240px] xl:left-[270px] top-[220px] xl:top-[240px] w-[220px] xl:w-[250px] z-20">
              <div className="p-5">
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg xl:text-xl font-bold text-gray-100 mb-2">
                    자율 유도비행 제어 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Autonomous Flight Control
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    AI 기반 자율 비행 제어로 복잡한 임무를 자동으로 수행할 수
                    있습니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute left-[480px] xl:left-[540px] top-[220px] xl:top-[240px] w-[220px] xl:w-[250px] z-20">
              <div className="p-5">
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg xl:text-xl font-bold text-gray-100 mb-2">
                    표적 자동인식 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Target Recognition
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    딥러닝 기반 표적 자동 인식으로 빠르고 정확한 식별이
                    가능합니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute left-[720px] xl:left-[810px] top-[220px] xl:top-[240px] w-[220px] xl:w-[250px] z-20">
              <div className="p-5">
                <div className="border-t-2 border-blue-500 pt-3">
                  <h3 className="text-lg xl:text-xl font-bold text-gray-100 mb-2">
                    군집비행 제어 기술
                  </h3>
                  <p className="text-xs text-blue-400 mb-3 uppercase tracking-wider">
                    Swarm Control
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    다수의 드론을 동시에 제어하여 협력 임무를 수행하는 군집 제어
                    기술입니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute right-0 bottom-[200px] z-5">
              <img
                src="/images/drone/OBJ2.png"
                alt="Main Drone System"
                className="w-[600px] xl:w-[800px] h-auto object-contain opacity-80"
                style={{ animation: 'float 3s ease-in-out infinite' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== ABOUT ===================== */}
      <main className="flex flex-col">
        <section
          id="about-section"
          className="w-full pt-16 md:pt-24 pb-20 md:pb-32 lg:pt-40 mx-auto bg-gray-950 bg-[url('/images/bg3.jpg')] bg-no-repeat bg-fixed bg-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950 pointer-events-none" />

          <div className="relative z-10">
            <div className="flex flex-col lg:flex-row px-6 lg:px-44">
              <div className="mb-8 lg:mb-0">
                <h2 className="mb-4 lg:mb-10 text-4xl md:text-5xl lg:text-6xl font-black text-blue-500 drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]">
                  KD darts
                </h2>
                <p className="text-sm md:text-base text-gray-400 max-w-md mb-8 lg:mb-0">
                  Drone AI Robotics Technology Solution
                </p>
              </div>

              <div className="lg:ml-40">
                <p className="mb-4 lg:mb-3  text-xs md:text-sm leading-tight font-semibold tracking-[0.15em] md:tracking-[0.2em] text-blue-400 uppercase">
                  Detection · Acquisition · Recognition · Tracking · Solution
                </p>
                <p className="mb-2 lg:mb-2 text-base md:text-lg font-medium text-gray-300 leading-relaxed tracking-tight">
                  KDDarts는 이름처럼, 목표를 정확히 포착하고 정보를 빠르게
                  확보하며, 상황을 명확히 이해하고 대상을 정교하게 추적하는 드론
                  AI 로보틱스 기술 개발을 목표로 합니다.
                </p>
                <p className="mb-2 lg:mb-2 text-base md:text-lg font-medium text-gray-300 leading-relaxed tracking-tight">
                  탐지·획득·인지·추적 전 과정을 유기적으로 통합해 드론 스스로
                  상황을 인지하고 대응함으로써 사용자의 간섭을 최소화 한 자율형
                  무인시스템을 제공합니다.
                </p>
                <p className="mb-2 lg:mb-3 text-xl md:text-2xl font-black text-gray-300 leading-relaxed tracking-tight">
                  더 빠르게, 더 똑똑하게, 더 정밀하게—
                </p>
                <p className="mb-0 lg:mb-6 text-base md:text-lg font-medium text-gray-300 leading-relaxed tracking-tight">
                  그 차이를 만들어내는 기술. 그 중심에 KD darts가 있습니다.
                </p>
              </div>
            </div>

            <div className="left-0 right-0 w-full m-auto ml-auto text-center px-6 lg:px-44">
              <div className="items-center p-6 md:p-8 lg:p-10 mt-8 bg-gradient-to-br from-gray-900/20 via-gray-800/30 to-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 opacity-50 pointer-events-none" />

                <h2 className="text-xl md:text-2xl font-bold text-center text-gray-100 p-6 md:p-10 relative">
                  KD DARTS CORE CAPABILITIES
                </h2>

                <div
                  ref={sectionRef}
                  className="flex flex-col justify-around pt-10 lg:flex-row gap-8 relative"
                >
                  {MAINIMAGE.map((image, index) => {
                    const isItemVisible = visibleItems.has(image.id);

                    return (
                      <div
                        key={image.id}
                        className={`mb-10 transform transition-all duration-700 ease-out ${
                          isItemVisible
                            ? 'translate-y-0 opacity-100 scale-100'
                            : 'translate-y-12 opacity-0 scale-95'
                        }`}
                        style={{
                          transitionDelay: `${index * 100}ms`,
                        }}
                      >
                        <div className="relative group">
                          <div
                            className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-full p-14 transition-all duration-500 border border-gray-700/50 ${
                              isItemVisible
                                ? 'shadow-[0_0_30px_rgba(59,130,246,0.3)] hover:shadow-[0_0_40px_rgba(59,130,246,0.5)] hover:scale-110'
                                : 'shadow-md'
                            }`}
                          >
                            <Image
                              src={image.imageSrc}
                              alt={image.title}
                              width="0"
                              height="0"
                              sizes="100vw"
                              style={{ width: image.width, height: 'auto' }}
                              className={`${image.className} transition-transform duration-300 group-hover:scale-110 brightness-110`}
                            />
                          </div>

                          <div
                            className={`absolute inset-0 rounded-full transition-all duration-700 bg-blue-500/20 blur-xl ${
                              isItemVisible
                                ? 'opacity-0 group-hover:opacity-100'
                                : 'opacity-0'
                            }`}
                            style={{
                              animationDelay: `${index * 200 + 500}ms`,
                            }}
                          />
                        </div>

                        <div
                          className={`pt-3 transition-all duration-500 ${
                            isItemVisible
                              ? 'translate-y-0 opacity-100'
                              : 'translate-y-4 opacity-0'
                          }`}
                          style={{
                            transitionDelay: `${index * 100 + 200}ms`,
                          }}
                        >
                          <h4 className="text-lg font-semibold text-gray-100 hover:text-blue-400 transition-colors duration-300">
                            {image.title}
                          </h4>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== NEWS ===================== */}
        {/* <aside
          id="news-section"
          className="relative px-4 sm:px-6 lg:px-44 bg-gray-900 pt-16 py-40 sm:pt-20 md:pt-24 pb-16 sm:pb-20"
        >
          <div
            className="absolute inset-0 bg-[url('/images/bg1.png')] bg-cover bg-center bg-no-repeat"
            style={{ zIndex: 0 }}
          />
          <div className="absolute inset-0 bg-black/60" style={{ zIndex: 1 }} />

          <div className="relative" style={{ zIndex: 2 }}>
            <div className="flex flex-col lg:flex-row lg:gap-16">
              <div className="lg:w-2/5 mb-12 lg:mb-0 lg:pt-0">
                <span className="text-blue-600 mb-4 text-sm md:text-base font-semibold tracking-wide block">
                  Welcome to the Visitor
                </span>
                <h2 className=" sm:text-4xl lg:text-5xl font-black text-gray-100 leading-tight mb-8">
                  AI와 항공 기술의 융합으로
                  <br />
                  미래를 선도합니다.
                </h2>
                <Link href="/news">
                  <button className="group flex items-center gap-2 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300">
                    <span className="font-semibold">News</span>
                    <svg
                      className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </Link>
              </div>

              <div className="lg:w-3/5 lg:pt-8">
                <MainCarousel carouselInfo={news} />
              </div>
            </div>
          </div>
        </aside> */}
      </main>

      <Footer />
    </div>
  );
}

const MAINIMAGE = [
  {
    id: 0,
    imageSrc: '/icon/camera3.png',
    title: 'VIS IMAGE',
    width: 150,
    height: 100,
    className:
      'absolute right-0 m-auto ml-auto mr-auto text-center left-1 bottom-1',
  },
  {
    id: 1,
    imageSrc: '/icon/ai.png',
    title: 'AI algorithm',
    width: 140,
    height: 100,
    className:
      'absolute right-2 m-auto ml-auto mr-auto text-center left-1 bottom-3',
  },
  {
    id: 2,
    imageSrc: '/icon/ircamera.png',
    title: 'IR IMAGE',
    width: 140,
    height: 140,
    className:
      'absolute right-0 m-auto ml-auto mr-auto text-center left-0 bottom-2',
  },
];
